<script setup>
import debounce from 'lodash/debounce';
import {onMounted, reactive, ref, watch} from 'vue';
import FormLeadCompanyCreate from '../../../components/forms/leadCompany/create.vue';

const loading = ref(true);
const leadCompanies = ref([]);
const search = ref('');
const creatingLeadCompany = ref(false);
const pagination = reactive({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
    from: 0,
    to: 0,
});

let currentRequest = null;

const loadLeadCompanies = async (page = pagination.current_page) => {
    currentRequest?.abort();
    const request = new AbortController();
    currentRequest = request;

    loading.value = true;

    try {
        const res = await axios.get(route('bo.api.v1.crm.lead-company.index'), {
            signal: request.signal,
            params: {
                page,
                search: search.value.trim(),
            },
        });

        // Aborting can't stop a response that has already resolved.
        if (request !== currentRequest) {
            return;
        }

        leadCompanies.value = res.data.lead_companies ?? [];
        pagination.current_page = res.data.pagination?.current_page ?? page;
        pagination.last_page = res.data.pagination?.last_page ?? 1;
        pagination.per_page = res.data.pagination?.per_page ?? 50;
        pagination.total = res.data.pagination?.total ?? leadCompanies.value.length;
        pagination.from = res.data.pagination?.from ?? 0;
        pagination.to = res.data.pagination?.to ?? leadCompanies.value.length;
    } catch (error) {
        if (request === currentRequest) {
            throw error;
        }
    } finally {
        if (request === currentRequest) {
            loading.value = false;
        }
    }
};

const goToPage = (page) => {
    if (page < 1 || page > pagination.last_page || page === pagination.current_page) {
        return;
    }

    loadLeadCompanies(page);
};

const debouncedSearch = debounce(() => {
    loadLeadCompanies(1);
}, 600);

const handleLeadCompanyCreated = (leadCompany) => {
    creatingLeadCompany.value = false;

    if (!leadCompany?.name) {
        loadLeadCompanies(1);
        return;
    }

    if (search.value === leadCompany.name) {
        loadLeadCompanies(1);
        return;
    }

    search.value = leadCompany.name;
};

watch(search, () => {
    debouncedSearch();
});

onMounted(() => {
    loadLeadCompanies();
});
</script>

<template>
    <div>
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ _mft('crm:leadCompany.title') }}</h1>
                <div class="flex items-center gap-3">
                    <button-primary @click="creatingLeadCompany = true">
                        {{ _mft('crm:leadCompany.createNewCompany') }}
                    </button-primary>
                </div>
            </div>
        </frosted-bar>

        <div
            class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
        >
            <div
                v-if="pagination.total > 0"
                class="text-xs text-gray-500"
            >
                {{ _mft('crm:leadCompany.showing', {
                    from: pagination.from,
                    to: pagination.to,
                    total: pagination.total,
                }) }}
            </div>
            <div class="w-full md:w-80 md:ml-auto">
                <input-text
                    v-model:content="search"
                    :placeholder="_mft('crm:leadCompany.searchPlaceholder')"
                    class="!mb-0"
                />
            </div>
        </div>

        <div
            v-if="loading"
            class="py-8 mt-4"
        >
            <mf-spinner-medium class="w-24 h-12" />
        </div>

        <list-basic v-if="!loading" class="mt-2">
            <list-basic-item v-if="leadCompanies.length === 0">
                <div class="py-2 text-sm text-gray-500">
                    {{ _mft('crm:leadCompany.empty') }}
                </div>
            </list-basic-item>

            <list-basic-item
                v-for="leadCompany in leadCompanies"
                :key="leadCompany.id"
            >
                <template #status>
                    <div class="w-6 h-6 mr-2 lg:mr-4">
                        <logo-from-domain
                            :email="leadCompany.primaryLead?.email"
                            :website="leadCompany.website"
                            :company-name="leadCompany.name"
                        />
                    </div>
                </template>
                <template #title>
                    <a
                        :href="route('crm.lead-company.single', leadCompany.id)"
                        class="mf-link font-medium"
                    >
                        {{ leadCompany.name }}
                    </a>
                </template>

                <template #actions>
                    <a :href="route('crm.lead-company.single', leadCompany.id)">
                        <button-secondary size="sm">
                            {{ _mft('crm:leadCompany.edit') }}
                        </button-secondary>
                    </a>
                </template>
            </list-basic-item>
        </list-basic>

        <div
            v-if="!loading && pagination.last_page > 1"
            class="mt-4 flex flex-wrap items-center justify-center gap-3"
        >
            <div class="flex items-center gap-2">
                <button-secondary
                    size="sm"
                    :disabled="pagination.current_page <= 1"
                    @click="goToPage(pagination.current_page - 1)"
                >
                    {{ _mft('crm:leadCompany.previous') }}
                </button-secondary>
                <span class="text-xs text-gray-500">
                    {{ _mft('crm:leadCompany.pageOf', {
                        page: pagination.current_page,
                        total: pagination.last_page,
                    }) }}
                </span>
                <button-secondary
                    size="sm"
                    :disabled="pagination.current_page >= pagination.last_page"
                    @click="goToPage(pagination.current_page + 1)"
                >
                    {{ _mft('crm:leadCompany.next') }}
                </button-secondary>
            </div>
        </div>

        <slideout-with-slots
            v-if="creatingLeadCompany"
            @close="creatingLeadCompany = false"
        >
            <template #title>
                <h2>{{ _mft('crm:leadCompany.createNewCompany') }}</h2>
            </template>

            <template #body>
                <form-lead-company-create @created="handleLeadCompanyCreated" />
            </template>
        </slideout-with-slots>
    </div>
</template>
